import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import SiteMessage from '../../../components/SiteMessage';

const MessageEditor = ({ message, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    message_type: 'info',
    variant: 'full',
    target_audience: ['all'],
    display_pages: ['all'],
    cta_text: '',
    cta_link: '',
    active: true,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    priority: 0,
    dismissible: true
  });

  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (message) {
      setFormData({
        title: message.title || '',
        content: message.content || '',
        message_type: message.message_type || 'info',
        variant: message.variant || 'full',
        target_audience: message.target_audience || ['all'],
        display_pages: message.display_pages || ['all'],
        cta_text: message.cta_text || '',
        cta_link: message.cta_link || '',
        active: message.active !== undefined ? message.active : true,
        start_date: message.start_date ? message.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
        end_date: message.end_date ? message.end_date.split('T')[0] : '',
        priority: message.priority || 0,
        dismissible: message.dismissible !== undefined ? message.dismissible : true
      });
    }
  }, [message]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      if (current.includes(value)) {
        // Don't allow removing if it's the only option
        if (current.length === 1) return prev;
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        // Remove 'all' when adding specific options
        if (value !== 'all' && current.includes('all')) {
          return { ...prev, [field]: [value] };
        }
        // Remove all other options when selecting 'all'
        if (value === 'all') {
          return { ...prev, [field]: ['all'] };
        }
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Title and content are required');
      return;
    }

    // Prepare data for submission
    const submitData = {
      ...formData,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
    };

    onSave(submitData);
  };

  const previewMessage = {
    id: 'preview',
    ...formData
  };

  const audienceOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'new_users', label: 'New Users (< 7 days)' },
    { value: 'free_users', label: 'Free Account Users' },
    { value: 'premium_users', label: 'Premium Subscribers' },
    { value: 'non_subscribers', label: 'Non-Subscribers' }
  ];

  const pageOptions = [
    { value: 'all', label: 'All Pages' },
    { value: 'registration', label: 'Registration Page' },
    { value: 'account_setup', label: 'Account Setup (SSO)' },
    { value: 'checkout', label: 'Subscription Checkout' },
    { value: 'subscription_preview', label: 'Subscription Preview' },
    { value: 'dashboard', label: 'User Dashboard' },
    { value: 'game_library', label: 'Game Library' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-foreground">
          {message ? 'Edit Message' : 'New Message'}
        </h2>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Icon name="Eye" size={16} className="mr-2" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Message'}
          </Button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-foreground mb-3">Preview</h3>
          <SiteMessage
            message={previewMessage}
            variant={formData.variant}
            onDismiss={formData.dismissible ? () => {} : undefined}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Basic Info */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-foreground">Message Content</h3>

            <Input
              label="Title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Play Everything Free Through March 31, 2026!"
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Content
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                rows={4}
                placeholder="Message content..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Message Type"
                value={formData.message_type}
                onChange={(value) => handleChange('message_type', value)}
                options={[
                  { value: 'success', label: 'Success (Green)' },
                  { value: 'info', label: 'Info (Blue)' },
                  { value: 'warning', label: 'Warning (Amber)' },
                  { value: 'error', label: 'Error (Red)' }
                ]}
              />

              <Select
                label="Variant"
                value={formData.variant}
                onChange={(value) => handleChange('variant', value)}
                options={[
                  { value: 'full', label: 'Full (Detailed)' },
                  { value: 'compact', label: 'Compact (Brief)' }
                ]}
              />
            </div>

            <Input
              label="CTA Text (Optional)"
              value={formData.cta_text}
              onChange={(e) => handleChange('cta_text', e.target.value)}
              placeholder="e.g., Learn More"
            />

            <Input
              label="CTA Link (Optional)"
              value={formData.cta_link}
              onChange={(e) => handleChange('cta_link', e.target.value)}
              placeholder="e.g., /pricing"
            />
          </div>

          {/* Settings */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-foreground">Settings</h3>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Active</label>
              <Checkbox
                checked={formData.active}
                onCheckedChange={(checked) => handleChange('active', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Dismissible</label>
              <Checkbox
                checked={formData.dismissible}
                onCheckedChange={(checked) => handleChange('dismissible', checked)}
              />
            </div>

            <Input
              label="Priority (higher = shown first)"
              type="number"
              value={formData.priority}
              onChange={(e) => handleChange('priority', parseInt(e.target.value) || 0)}
              min={0}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Targeting */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-foreground">Target Audience</h3>
            <div className="space-y-2">
              {audienceOptions.map(option => (
                <div key={option.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.target_audience.includes(option.value)}
                    onCheckedChange={() => handleArrayToggle('target_audience', option.value)}
                  />
                  <label className="text-sm text-foreground">{option.label}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-foreground">Display Pages</h3>
            <div className="space-y-2">
              {pageOptions.map(option => (
                <div key={option.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.display_pages.includes(option.value)}
                    onCheckedChange={() => handleArrayToggle('display_pages', option.value)}
                  />
                  <label className="text-sm text-foreground">{option.label}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Scheduling */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-foreground">Schedule</h3>

            <Input
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              required
            />

            <Input
              label="End Date (Optional)"
              type="date"
              value={formData.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              min={formData.start_date}
            />

            <p className="text-xs text-muted-foreground">
              Leave end date empty for messages that run indefinitely
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MessageEditor;
